import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import styles from '../MagicSignTable.module.scss';
import FilteredIconMU from '../../../../../assets/Svg/FilteredIconMU';

interface ColumnFilterDropdownProps {
    label: string;
    options: { id: string; label: string }[];
    selected: string[];
    onApply: (selected: string[]) => void;
}

/**
 * Multi-select column filter dropdown.
 * Uses FilteredIconMU (the funnel icon) as the filter affordance — same component
 * and same colour treatment as MagicUpload's FilteredHeader, so the two tables
 * look identical to users.
 */
const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({ label, options, selected, onApply }) => {
    const [open, setOpen] = useState(false);
    const [localSelected, setLocalSelected] = useState<string[]>(selected);
    const ref = useRef<HTMLDivElement>(null);

    const handleClickOutside = React.useCallback(
        (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setLocalSelected(selected);
            }
        },
        [selected],
    );

    React.useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, handleClickOutside]);

    React.useEffect(() => {
        setLocalSelected(selected);
    }, [selected]);

    const toggle = (id: string) => {
        setLocalSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const isActive = selected.length > 0;

    return (
        <div className={styles.ColFilterContainer} ref={ref}>
            <button
                className={clsx(styles.ColFilterBtn, { [styles.ColFilterBtnActive]: isActive })}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                }}
                aria-label={`Filter by ${label}`}
            >
                {label}
                <FilteredIconMU
                    colour={isActive || open ? 'var(--primary-text-colour)' : '#B0B0B0'}
                    width="14"
                    height="10"
                    style={{ marginLeft: '0.25rem', flexShrink: 0 }}
                />
            </button>
            {open && (
                <div className={styles.ColFilterPopover} role="listbox" aria-label={`${label} filter options`}>
                    {options.map((opt) => (
                        <label key={opt.id} className={styles.ColFilterOption}>
                            <input
                                type="checkbox"
                                checked={localSelected.includes(opt.id)}
                                onChange={() => toggle(opt.id)}
                                aria-checked={localSelected.includes(opt.id)}
                            />
                            <span>{opt.label}</span>
                        </label>
                    ))}
                    <div className={styles.ColFilterActions}>
                        <button
                            className={styles.ColFilterApply}
                            onClick={(e) => {
                                e.stopPropagation();
                                onApply(localSelected);
                                setOpen(false);
                            }}
                        >
                            Apply
                        </button>
                        <button
                            className={styles.ColFilterClear}
                            onClick={(e) => {
                                e.stopPropagation();
                                setLocalSelected([]);
                                onApply([]);
                                setOpen(false);
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnFilterDropdown;
